import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Expand } from "lucide-react";
import Lightbox from "@/components/Lightbox";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

const RESIDENCES = [
  {
    id: "de-la-vie",
    num: "01",
    name: "De La Vie Maison",
    tagline: "Space to Live. Room to Stay.",
    location: "Baker, Louisiana",
    links: {
      airbnb: "https://airbnb.com/h/parkerfurnishedstaysdelavie",
      furnishedFinder: "https://www.furnishedfinder.com/property/998961_2",
    },
    theme: "dark",
    desc: "A grand four-bedroom residence with an optional bunk room and two living rooms — made for groups, teams and professionals who want room to gather without giving up an ounce of comfort.",
    specs: [
      ["Bedrooms", "4 + Optional Bunk Room"],
      ["Bathrooms", "2–3"],
      ["Living", "Two Living Rooms"],
      ["Sleeps", "8+ Guests"],
      ["Location", "Baker, LA"],
    ],
    images: [
      { src: "/images/p-delavie-living.jpg", ratio: 1.756, alt: "De La Vie Maison living room with brick fireplace wall" },
      { src: "/images/p-delavie-bed1.jpg", ratio: 2.091, alt: "Bedroom one with bold blue bedding" },
      { src: "/images/p-delavie-bed2.jpg", ratio: 2.091, alt: "Bedroom two with gold headboard" },
      { src: "/images/p-delavie-bed3.jpg", ratio: 2.307, alt: "Bedroom three with green drapery" },
      { src: "/images/p-delavie-bed4.jpg", ratio: 2.307, alt: "Bedroom four with red accent pillows" },
      { src: "/images/p-delavie-bunk.jpg", ratio: 1.500, alt: "Optional bunk room with four navy-dressed beds" },
      { src: "/images/p-delavie-kitchen.jpg", ratio: 1.333, alt: "Kitchen with butcher-block island and hanging pot rack" },
      { src: "/images/p-delavie-living2.jpg", ratio: 1.333, alt: "Second living room with large smart TV" },
      { src: "/images/p-delavie-kitchen2.jpg", ratio: 0.750, alt: "Kitchen and dining area overlooking the grounds" },
      { src: "/images/p-delavie-bath.jpg", ratio: 0.750, alt: "Bathroom with brick floors and full-length mirror" },
      { src: "/images/p-delavie-bath2.jpg", ratio: 0.750, alt: "Upstairs bathroom with sloped ceiling and wood floors" },
      { src: "/images/p-delavie-bath3.jpg", ratio: 0.750, alt: "Upstairs bathroom vanity and walk-in shower" },
      { src: "/images/p-delavie-foyer.jpg", ratio: 0.750, alt: "Foyer staircase with chandelier and brick floors" },
    ],
  },
  {
    id: "heritage",
    num: "02",
    name: "Heritage",
    tagline: "Room to Gather. Space to Settle In.",
    location: "Ascension Parish, Louisiana",
    links: {
      airbnb: "https://airbnb.com/h/parkerfurnishedstaysheritagecottage",
      furnishedFinder: "https://www.furnishedfinder.com/property/708598_1",
    },
    theme: "light",
    desc: "Three bedrooms and seven beds across generous living areas — the natural choice for teams, crews and extended stays who work better and stay brighter.",
    specs: [
      ["Bedrooms", "3 Bedrooms · 7 Beds"],
      ["Bathrooms", "2"],
      ["Living", "Spacious Living Areas"],
      ["Ideal For", "Teams, Crews & Extended Stays"],
      ["Location", "Ascension Parish, LA"],
    ],
    images: [
      { src: "/images/p-heritage-living.jpg", ratio: 1.608, alt: "Heritage living room with large grey sectional sofa" },
      { src: "/images/p-heritage-bed1.jpg", ratio: 1.391, alt: "Heritage bedroom one with blue plaid bedding" },
      { src: "/images/p-heritage-bed2.jpg", ratio: 1.391, alt: "Heritage bedroom two with twin beds" },
      { src: "/images/p-heritage-bed3.jpg", ratio: 1.391, alt: "Heritage bedroom three with two beds" },
      { src: "/images/p-coll-heritage.jpg", ratio: 0.772, alt: "Heritage lounge corner with sectional and bookcases" },
      { src: "/images/p-heritage-kitchen.jpg", ratio: 1.333, alt: "Heritage kitchen and dining area with dark wood cabinetry" },
      { src: "/images/p-heritage-kitchen2.jpg", ratio: 1.333, alt: "Heritage kitchen with stainless appliances and island range" },
      { src: "/images/p-heritage-bath.jpg", ratio: 0.750, alt: "Heritage bathroom with dark wood vanity and granite counter" },
      { src: "/images/p-heritage-bath2.jpg", ratio: 0.750, alt: "Heritage bathroom with tub, shower and double-door cabinet" },
    ],
  },
  {
    id: "hearthstead",
    num: "03",
    name: "Hearthstead Cottage",
    tagline: "Private. Refined. Effortlessly Comfortable.",
    location: "Baker, Louisiana",
    links: {
      airbnb: "https://airbnb.com/h/parkerfurnishedstayshearthsteadcottage",
      furnishedFinder: "https://www.furnishedfinder.com/property/998961_1",
    },
    theme: "dark",
    desc: "A quieter kind of furnished living on a gated estate — two bedrooms plus a loft, made for those who like their comfort to feel like home.",
    specs: [
      ["Bedrooms", "2 + Loft"],
      ["Bathroom", "1"],
      ["Sleeps", "4 Guests"],
      ["Parking", "2 Spaces"],
      ["Setting", "Gated Estate · Baker, LA"],
    ],
    images: [
      { src: "/images/p-hearth-suite.jpg", ratio: 1.500, alt: "Hearthstead Cottage bedroom with arched mirrors and dark wood bed" },
      { src: "/images/p-hearth-master.jpg", ratio: 1.937, alt: "Hearthstead Cottage master bedroom with navy bench" },
      { src: "/images/p-hearth-bed2.jpg", ratio: 1.333, alt: "Hearthstead Cottage second bedroom with smart TV" },
      { src: "/images/p-hearth-kitchen.jpg", ratio: 0.750, alt: "Hearthstead Cottage kitchen with stainless steel appliances" },
      { src: "/images/p-hearth-shower.jpg", ratio: 0.750, alt: "Hearthstead Cottage walk-in shower with glass door" },
      { src: "/images/p-hearth-bath.jpg", ratio: 0.750, alt: "Hearthstead Cottage bathroom with marble-topped vanity" },
      { src: "/images/p-hearth-dining.jpg", ratio: 0.750, alt: "Hearthstead Cottage dining area and living room with glass table" },
      { src: "/images/p-hearth-living.jpg", ratio: 1.333, alt: "Hearthstead Cottage living room with sofas, smart TV and garden doors" },
      { quote: "A quieter kind of furnished living." },
    ],
  },
];

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, delay, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function QuoteTile({ text, testid, className = "" }) {
  return (
    <div data-testid={testid} className={`border border-[#16301f]/20 p-1.5 ${className}`}>
      <div className="h-full bg-[#1a3324] flex items-center justify-center px-8 text-center">
        <p className="font-script text-3xl sm:text-4xl text-[#f7f2e7]">{text}</p>
      </div>
    </div>
  );
}

function GalleryImage({ img, className = "", testid, onOpen, natural = false }) {
  const [ratio, setRatio] = useState(img.ratio || 1.5);
  return (
    <div
      data-testid={testid}
      onClick={onOpen}
      className={`group border border-[#16301f]/20 p-1.5 cursor-zoom-in ${className}`}
    >
      <div className="relative overflow-hidden h-full" style={natural ? { aspectRatio: Math.max(ratio, 1) } : undefined}>
        <img
          src={img.src}
          alt={img.alt}
          loading="lazy"
          onLoad={(e) => {
            if (!img.ratio && e.target.naturalWidth) setRatio(e.target.naturalWidth / e.target.naturalHeight);
          }}
          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
        />
        <div className="absolute inset-0 bg-[#16301f]/0 group-hover:bg-[#16301f]/25 transition-colors duration-500 flex items-center justify-center">
          <Expand
            className="w-6 h-6 text-[#f7f2e7] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            strokeWidth={1.5}
          />
        </div>
      </div>
    </div>
  );
}

export default function Residences({ onEnquire }) {
  const [lightbox, setLightbox] = useState(null);

  const openLightbox = (photos, src) =>
    setLightbox({ photos, index: Math.max(0, photos.findIndex((p) => p.src === src)) });

  const [uploads, setUploads] = useState([]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/photos`)
      .then((r) => r.json())
      .then(setUploads)
      .catch(() => {});
  }, []);

  return (
    <section id="residences" data-testid="residences-section">
      {RESIDENCES.map((r, idx) => {
        const alt = idx % 2 === 1;
        const uploaded = uploads
          .filter((u) => u.property === r.id)
          .map((u) => ({ src: u.src, alt: u.alt, ratio: u.ratio }));
        const allImages = [...r.images, ...uploaded];
        return (
          <article
            key={r.id}
            id={r.id}
            data-testid={`residence-${r.id}`}
            className={alt ? "bg-[#fbf8f0]/90 text-[#16301f]" : "bg-[#f7f2e7]/90 text-[#16301f]"}
          >
            <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
              <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-end mb-12">
                <Reveal className="lg:col-span-8">
                  <p className="text-xs uppercase tracking-[0.3em] text-[#16301f]/60 flex items-center gap-4 mb-6">
                    <span className="font-display text-4xl sm:text-5xl text-[#16301f]/25">{r.num}</span>
                    <span className="h-px flex-1 bg-[#16301f]/20" />
                    {r.location}
                  </p>
                  <h2 className="font-display font-medium text-4xl sm:text-5xl leading-[1.05]">{r.name}</h2>
                  <p className="font-script text-3xl sm:text-4xl text-[#1a3324] mt-3 -rotate-1 inline-block">
                    {r.tagline}
                  </p>
                </Reveal>
                <Reveal delay={0.15} className="lg:col-span-4">
                  <p className={`text-sm leading-relaxed font-light text-[#16301f]/65`}>
                    {r.desc}
                  </p>
                  <button
                    data-testid={`${r.id}-enquire-btn`}
                    onClick={() => {
                      onEnquire(r.name);
                      scrollToId("#inquiry");
                    }}
                    className="group mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] pb-1 border-b border-[#16301f]/40 text-[#16301f] hover:border-[#16301f] transition-colors duration-300"
                  >
                    Enquire about {r.name}
                    <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#16301f]/45 mt-7">
                    Or book instantly
                  </p>
                  <div className="flex flex-wrap gap-x-6 gap-y-2 mt-2.5">
                    <a
                      data-testid={`${r.id}-airbnb-link`}
                      href={r.links.airbnb}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#16301f]/70 hover:text-[#16301f] transition-colors duration-300"
                    >
                      Airbnb <ArrowUpRight className="w-3.5 h-3.5" />
                    </a>
                    <a
                      data-testid={`${r.id}-ff-link`}
                      href={r.links.furnishedFinder}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#16301f]/70 hover:text-[#16301f] transition-colors duration-300"
                    >
                      Furnished Finder <ArrowUpRight className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </Reveal>
              </div>

              <Reveal delay={0.1}>
                <div
                  data-testid={`${r.id}-specs`}
                  className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 border-y border-[#16301f]/15 divide-[#16301f]/10 divide-x mb-12"
                >
                  {r.specs.map(([label, value]) => (
                    <div key={label} className="px-4 py-5">
                      <p className="text-[10px] uppercase tracking-[0.25em] text-[#16301f]/50">{label}</p>
                      <p className="font-display text-base sm:text-lg mt-1.5 leading-snug">{value}</p>
                    </div>
                  ))}
                </div>
              </Reveal>

              {(() => {
                const rest = allImages.slice(1);
                const photos = allImages.filter((i) => !i.quote);
                const candidates = rest.filter((i) => !i.quote).slice(0, 4);
                const panoramic = candidates.length >= 2 && candidates.slice(0, 2).every((i) => (i.ratio || 1.5) >= 1.7);
                const gridItems = panoramic ? candidates.slice(0, 2) : candidates;
                const bottomRow = rest.filter((i) => !gridItems.includes(i));
                return (
                  <>
                    <div className="grid lg:grid-cols-12 gap-4 sm:gap-5 items-center">
                      <Reveal className="lg:col-span-7">
                        <GalleryImage
                          img={allImages[0]}
                          natural
                          testid={`${r.id}-image-main`}
                          onOpen={() => openLightbox(photos, allImages[0].src)}
                        />
                      </Reveal>
                      <div className={`lg:col-span-5 grid ${panoramic ? "grid-cols-1" : "grid-cols-2"} gap-4 sm:gap-5 content-center`}>
                        {gridItems.map((img, i) => (
                          <Reveal key={img.src} delay={0.1 + i * 0.06}>
                            <GalleryImage
                              img={img}
                              natural
                              testid={`${r.id}-image-${i + 2}`}
                              onOpen={() => openLightbox(photos, img.src)}
                            />
                          </Reveal>
                        ))}
                      </div>
                    </div>
                    {bottomRow.length > 0 && (
                      <div data-testid={`${r.id}-gallery-more`} className="columns-2 lg:columns-3 gap-4 sm:gap-5 mt-4 sm:mt-5">
                        {bottomRow.map((img, i) => (
                          <Reveal key={img.src || img.quote} delay={(i % 3) * 0.06} className="break-inside-avoid mb-4 sm:mb-5">
                            {img.quote ? (
                              <QuoteTile
                                text={img.quote}
                                testid={`${r.id}-quote-tile`}
                                className="h-[180px] sm:h-[220px]"
                              />
                            ) : (
                              <GalleryImage
                                img={img}
                                natural
                                testid={`${r.id}-image-extra-${i + 1}`}
                                onOpen={() => openLightbox(photos, img.src)}
                              />
                            )}
                          </Reveal>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </article>
        );
      })}
      {lightbox && (
        <Lightbox
          photos={lightbox.photos}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onNav={(dir) =>
            setLightbox((lb) => ({
              ...lb,
              index: (lb.index + dir + lb.photos.length) % lb.photos.length,
            }))
          }
        />
      )}
    </section>
  );
}
